import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ISupportedProcessorSettings } from '../shared/interfaces/supported-processor-setting.interface';
import { ES_CONSTANTS as CONSTANTS } from '@portal/entity-services/interfaces';
import { UserService } from '@portal/shared/user';
import { IFilterVisibility } from '../shared/interfaces/filter-visibility.interface';

@Injectable({ providedIn: 'root' })
export class SupportedProcessorSettingsService {
  isProcessorFetchedSubject = new Subject();
  isProcessorFetched$ = this.isProcessorFetchedSubject.asObservable();
  private filterVisibility: IFilterVisibility = {
    isCurrencyVisible: true,
    isTransactionTypeVisible: true,
    isProductVisible: true,
    isProcessorVisible: true,
    isWalletVisible: true,
    isTransactionStateVisible: true,
    isFallbackModeVisible: false,
  };
  constructor(private http?: HttpClient, private userService?: UserService) {}

  getSupportedProcessorSettings(entityUid: string = ''): Observable<ISupportedProcessorSettings> {
    if (!entityUid) {
      entityUid = this.userService.getEntityUid();
    }
    const url = `${CONSTANTS.ENTITY_SERVICE.ORGANISATION}${entityUid}/supportedProcessorSettings`;
    return this.http.get<ISupportedProcessorSettings>(url).pipe(map((response) => response));
  }

  setSupportedProcessorSettingsInSessionStorage(
    supportedProcessorSettings: ISupportedProcessorSettings,
  ): void {
    localStorage.setItem(
      'supported_processor_settings',
      JSON.stringify(supportedProcessorSettings),
    );
  }

  getSupportedProcessorSettingsFromSessionStorage(): ISupportedProcessorSettings {
    const supportedProcessorSettings = localStorage.getItem('supported_processor_settings');
    return JSON.parse(supportedProcessorSettings);
  }

  isSingleCurrencyConfigured(): boolean | null {
    const settings = this.getSupportedProcessorSettingsFromSessionStorage();
    if (!settings) {
      return null;
    }

    const currencies = settings?.currenciesSupported;
    return currencies?.length === 1;
  }

  removeSupportedProcessorSettingsFromSessionStorage(): void {
    localStorage.removeItem('supported_processor_settings');
  }

  getEfbModesSupported(): string[] {
    const settings = this.getSupportedProcessorSettingsFromSessionStorage();
    return settings?.efbModesSupported || [];
  }

  /**
   * Get the appropriate processors list based on availability
   * Returns destinationProcessorsSupported if available, otherwise processorsSupported
   */
  getProcessorsSupported(): string[] {
    const settings = this.getSupportedProcessorSettingsFromSessionStorage();
    // Prioritize destinationProcessorsSupported if it exists
    if (settings?.destinationProcessorsSupported && settings.destinationProcessorsSupported.length > 0) {
      return settings.destinationProcessorsSupported;
    }
    // Fall back to processorsSupported
    return settings?.processorsSupported || [];
  }

  getFilterVisibility(): IFilterVisibility {
    // Always update visibility based on current session storage before returning
    this.updateFilterVisibility();
    return this.filterVisibility;
  }
  /** When the supported processor settings configuration for an EntityId is empty,
   * we don't limit filter items, we show them all as default behavior,
   * When in the configuration we have just one element we don't display on the UI,
   */
  updateFilterVisibility(): void {
    const supportedProcessorSettings = this.getSupportedProcessorSettingsFromSessionStorage();

    // Return early if no settings available (e.g., after error or on initial load)
    if (!supportedProcessorSettings) {
      return;
    }

    const supportedProcessorSettingsFilterMapping = {
      isCurrencyVisible: 'currenciesSupported',
      isTransactionTypeVisible: 'transactionTypesSupported',
      isProductVisible: 'paymentTypesSupported',
      isWalletVisible: 'walletsSupported',
      isTransactionStateVisible: 'transactionStatesSupported',
    };

    // Handle other filters (excluding processor for now)
    Object.keys(supportedProcessorSettingsFilterMapping).forEach((filterKey) => {
      const settingKey = supportedProcessorSettingsFilterMapping[filterKey];
      const setting = supportedProcessorSettings[settingKey];
      if (setting?.length === 1) {
        this.filterVisibility[filterKey] = false;
      }
    });

    // Handle processor filter visibility
    // Use getProcessorsSupported() which already handles the priority logic
    const processors = this.getProcessorsSupported();
    // Show filter if 0 (shows all) or 2+ processors, hide if exactly 1
    this.filterVisibility.isProcessorVisible = processors.length !== 1;

    // Handle fallback mode visibility based on efbModesSupported
    if (supportedProcessorSettings?.efbModesSupported && supportedProcessorSettings.efbModesSupported.length > 0) {
      this.filterVisibility.isFallbackModeVisible = true;
    } else {
      this.filterVisibility.isFallbackModeVisible = false;
    }
  }
}
